import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import * as Fonts from "../../components/Fonts";
import { KeepoLoader } from "../../components/KeepoLoader";
import { Skeleton } from "../../components/Skeleton";
import { ScreenHeader, SubHeader } from "../../components/SubHeader";
import ChevronRightIcon from "../../icons/ChevronRight";
import { getBalance, getHistory, PaymentBalance, PaymentHistoryItem, PaymentsState } from "../../services/payments";
import { formatCents } from "../Agenda/format";
import { MenuCard, MenuList, MenuStatus } from "../Agenda/styles";
import { SmallButton } from "../Domains/styles";
import { Card, Container, Section } from "../Settings/styles";
import { FeesAndTerms } from "./FeesAndTerms";
import { History } from "./History";
import { recipientStatusKey } from "./recipientStatus";
import { Registration } from "./Registration";
import { chargesPrefix, keepoNames, PaymentsLoad, prefix, usePayments } from "./state";
import { Amount, Center, Message, Pair, Row, Status } from "./styles";
import { WITHDRAW_TEST_MODE, WithdrawModal } from "./WithdrawModal";

/** Cobranças' pages, by their address (/cobrancas/<page>). */
export const CHARGES_PAGES = ["movimentacoes", "taxas", "dados-bancarios"] as const;
export type ChargesPageKey = (typeof CHARGES_PAGES)[number];

const TITLES: Record<ChargesPageKey, string> = {
  movimentacoes: "history",
  taxas: "fees",
  "dados-bancarios": "bank",
};

type Tone = "on" | "waiting" | "off";

// Icons in the same strokes as the Keepo's other ones
const Icon = ({ children }: { children: ReactNode }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {children}
  </svg>
);

const ICONS: Record<ChargesPageKey, ReactNode> = {
  // arrows both ways: money in and out
  movimentacoes: (
    <Icon>
      <path d="M7 4v16M7 4 3 8M7 4l4 4M17 20V4M17 20l-4-4M17 20l4-4" />
    </Icon>
  ),
  // a percent sign
  taxas: (
    <Icon>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </Icon>
  ),
  // a bank
  "dados-bancarios": (
    <Icon>
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
    </Icon>
  ),
};

/**
 * Cobranças: what the Keepo sells online and receives. On top, a dashboard (the balance with Sacar, and the month's
 * sales) once the registration is approved, or how the registration is going; under it, its three pages.
 */
const ChargesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { siteId, payments } = usePayments();
  const approved = typeof payments === "object" && payments.eligible && payments.recipient?.status === "approved";
  // undefined: loading (or not approved: none); "error": couldn't load
  const [balance, setBalance] = useState<PaymentBalance | "error">();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!approved) return;
    let isCurrent = true;
    setBalance(undefined);
    getBalance(siteId)
      .then(value => isCurrent && setBalance(value))
      .catch(() => isCurrent && setBalance("error"));
    return () => {
      isCurrent = false;
    };
  }, [siteId, approved, reloadKey]);

  const status = (key: ChargesPageKey): [Tone, string] | undefined => {
    if (typeof payments !== "object") return undefined;
    if (key === "movimentacoes") {
      if (!approved) return ["off", t(`${chargesPrefix}.menu.noSales`)];
      if (typeof balance !== "object") return undefined;
      return balance.month.count > 0
        ? ["on", t(`${chargesPrefix}.menu.monthSales`, { count: balance.month.count })]
        : ["off", t(`${chargesPrefix}.menu.noSales`)];
    }
    if (key === "taxas") {
      return [
        "on",
        t(`${chargesPrefix}.menu.${payments.account.feesPayer === "owner" ? "ownerPays" : "customerPays"}`),
      ];
    }
    const recipient = payments.recipient;
    if (!recipient) return ["off", t(`${chargesPrefix}.menu.noRegistration`)];
    const tone: Tone = recipient.status === "approved" ? "on" : recipient.status === "refused" ? "off" : "waiting";
    return [tone, t(`${prefix}.status.${recipientStatusKey(recipient)}`)];
  };

  const notEligible =
    payments === "unavailable" || payments === "error" || (typeof payments === "object" && !payments.eligible);

  return (
    <Container>
      <SubHeader title={t(`${chargesPrefix}.title`)} crumbs={[]} rightElement={<TermsLink />} />
      {approved ? (
        <Dashboard
          siteId={siteId}
          balance={balance}
          reloadKey={reloadKey}
          cardReleaseDays={typeof payments === "object" ? payments.cardReleaseDays : 30}
          bankLabel={typeof payments === "object" ? (payments.recipient?.bankLabel ?? null) : null}
          onWithdrawn={() => setReloadKey(key => key + 1)}
        />
      ) : (
        <RegistrationSummary payments={payments} onOpen={() => navigate("dados-bancarios")} />
      )}
      {!notEligible && (
        <Section>
          <MenuList>
            {CHARGES_PAGES.map(key => {
              const current = status(key);
              return (
                <li key={key}>
                  <MenuCard type="button" onClick={() => navigate(key)}>
                    <span className="icon" aria-hidden="true">
                      {ICONS[key]}
                    </span>
                    <span className="text">
                      <span className="title">{t(`${chargesPrefix}.pages.${TITLES[key]}`)}</span>
                      <span className="description">{t(`${chargesPrefix}.menu.descriptions.${TITLES[key]}`)}</span>
                      {current ? (
                        <MenuStatus $tone={current[0]}>{current[1]}</MenuStatus>
                      ) : (
                        <Skeleton className="status" aria-hidden="true" />
                      )}
                    </span>
                    <span className="chevron" aria-hidden="true">
                      <ChevronRightIcon width={14} height={14} color="currentColor" />
                    </span>
                  </MenuCard>
                </li>
              );
            })}
          </MenuList>
        </Section>
      )}
    </Container>
  );
};

/** The terms of Cobranças, on the right of each of its screens' title */
const TermsLink = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <TermsButton type="button" onClick={() => navigate("/cobrancas/termos")}>
      {t(`${chargesPrefix}.termsLink`)}
    </TermsButton>
  );
};

/** Before the registration is approved: how it is going, and the way to it (or why there's none). */
const RegistrationSummary = ({ payments, onOpen }: { payments: PaymentsLoad; onOpen: () => void }) => {
  const { t } = useTranslation();

  const content = () => {
    if (payments === undefined) {
      return (
        <Card role="status" aria-label={t(`${prefix}.loading`)}>
          <Center>
            <KeepoLoader size={24} color="var(--color-neutral-white)" />
          </Center>
        </Card>
      );
    }
    if (payments === "unavailable" || payments === "error" || !payments.eligible) {
      const message = typeof payments === "string" ? payments : "notEligible";
      return (
        <Card>
          <Message>{t(`${prefix}.${message === "error" ? "loadError" : message}`)}</Message>
        </Card>
      );
    }
    const recipient = payments.recipient;
    return (
      <Card>
        <Row>
          <div className="text">
            <p className="title">{t(`${chargesPrefix}.registration.${recipient ? "title" : "start"}`)}</p>
            {recipient ? (
              <Status $status={recipient.status}>{t(`${prefix}.status.${recipientStatusKey(recipient)}`)}</Status>
            ) : (
              <p className="description">{t(`${chargesPrefix}.registration.intro`)}</p>
            )}
          </div>
          <SmallButton type="button" $primary onClick={onOpen}>
            {t(`${chargesPrefix}.registration.${recipient ? "open" : "begin"}`)}
          </SmallButton>
        </Row>
      </Card>
    );
  };

  return (
    <Section>
      <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>
      {content()}
    </Section>
  );
};

/** The registration's balance (shared by its Keepos) with Sacar. */
const Dashboard = ({
  siteId,
  balance,
  reloadKey,
  cardReleaseDays,
  bankLabel,
  onWithdrawn,
}: {
  siteId: string;
  balance: PaymentBalance | "error" | undefined;
  /** Changes after a withdrawal: the one on its way is looked up again */
  reloadKey: number;
  cardReleaseDays: number;
  /** "Bradesco ••24": where the withdrawal goes */
  bankLabel: string | null;
  onWithdrawn: () => void;
}) => {
  const { t } = useTranslation();
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [requested, setRequested] = useState(false);
  const money = (cents: number) => formatCents(cents, "BRL");
  // the withdrawal still on its way (the newest open one among the withdrawals): it shows under "A receber"
  const [openWithdrawal, setOpenWithdrawal] = useState<PaymentHistoryItem>();

  useEffect(() => {
    let isCurrent = true;
    getHistory(siteId, null, { types: ["withdrawal"] })
      .then(page => {
        const open = page.items.find(
          item => item.type === "withdrawal" && ["pending", "processing", "review"].includes(item.status)
        );
        if (isCurrent) setOpenWithdrawal(open);
      })
      .catch(() => undefined);
    return () => {
      isCurrent = false;
    };
  }, [siteId, reloadKey]);

  const content = () => {
    if (balance === undefined) {
      return (
        <Card role="status" aria-label={t(`${prefix}.balance.loading`)}>
          <Center>
            <KeepoLoader size={24} color="var(--color-neutral-white)" />
          </Center>
        </Card>
      );
    }
    if (balance === "error") {
      return (
        <Card>
          <Message>{t(`${prefix}.balance.loadError`)}</Message>
        </Card>
      );
    }
    // Local only (yarn dev): Sacar always opens, with a test balance, to try the screen (nothing is withdrawn)
    const canWithdraw =
      WITHDRAW_TEST_MODE ||
      (balance.availableCents >= balance.minWithdrawCents && balance.availableCents > balance.withdrawFeeCents);
    const withdrawButton = (
      <SmallButton
        type="button"
        $primary={canWithdraw}
        disabled={!canWithdraw}
        onClick={() => {
          setRequested(false);
          setShowWithdraw(true);
        }}>
        {t(`${prefix}.balance.withdraw`)}
      </SmallButton>
    );
    const notes = (
      <>
        {!canWithdraw && (
          <Message>{t(`${prefix}.balance.belowMinimum`, { min: money(balance.minWithdrawCents) })}</Message>
        )}
        {balance.sharedWith.length > 0 && (
          <Message>{t(`${prefix}.balance.sharedWith`, { keepos: keepoNames(balance.sharedWith, t) })}</Message>
        )}
        {requested && <Message role="status">{t(`${prefix}.balance.requested`)}</Message>}
      </>
    );
    const waitingHint = t(`${chargesPrefix}.balance.waitingHint`, { count: cardReleaseDays });

    return (
      <Pair>
        <Card>
          <Amount>
            <span className="label title">{t(`${prefix}.balance.available`)}</span>
            <span className="value">{money(balance.availableCents)}</span>
          </Amount>
          {notes}
          <div>{withdrawButton}</div>
        </Card>
        <Card>
          <Amount>
            <span className="label title">{t(`${prefix}.balance.waiting`)}</span>
            <span className="value">{money(balance.waitingCents)}</span>
            <span className="detail">{waitingHint}</span>
          </Amount>
          {openWithdrawal && (
            <InProgress>
              {t(`${chargesPrefix}.balance.inProgress`, { amount: money(openWithdrawal.paidCents ?? 0) })}
            </InProgress>
          )}
        </Card>
      </Pair>
    );
  };

  return (
    <Section>
      <Fonts.Label>{t(`${prefix}.balance.section`)}</Fonts.Label>
      {content()}
      {showWithdraw && typeof balance === "object" && (
        <WithdrawModal
          siteId={siteId}
          balance={balance}
          bankLabel={bankLabel}
          onDone={() => {
            setShowWithdraw(false);
            setRequested(true);
            onWithdrawn();
          }}
          onClose={() => setShowWithdraw(false)}
        />
      )}
    </Section>
  );
};

/** One page of Cobranças: its title, the way back, and its content. */
export const ChargesSubPage = ({ page, isRegistering = false }: { page: ChargesPageKey; isRegistering?: boolean }) => {
  const { t } = useTranslation();
  const { siteId, payments, setPayments, isCurrent } = usePayments();
  const update = (state: PaymentsState) => setPayments(state);
  // After a refusal the form starts again, and says so (as the button that opened it)
  const startAgain = typeof payments === "object" && payments.recipient?.status === "refused";

  // Adding bank details: a flow of its own, as creating a Keepo (steps on top, fixed bar at the bottom)
  if (isRegistering) {
    return (
      <FlowContainer>
        <Registration
          siteId={siteId}
          payments={payments}
          setPayments={update}
          isCurrent={isCurrent}
          isRegistering
          header={
            <ScreenHeader
              title={t(`${prefix}.${startAgain ? "startAgain" : "start"}`)}
              back={{ label: t(`${chargesPrefix}.pages.bank`), to: "/cobrancas/dados-bancarios" }}
              rightElement={<TermsLink />}
            />
          }
        />
      </FlowContainer>
    );
  }

  return (
    <Container>
      <SubHeader
        title={t(`${chargesPrefix}.pages.${TITLES[page]}`)}
        crumbs={[{ label: t(`${chargesPrefix}.title`), to: "/cobrancas" }]}
        rightElement={<TermsLink />}
      />
      {page === "movimentacoes" && siteId && <HistoryPage siteId={siteId} payments={payments} />}
      {page === "taxas" && (
        <FeesAndTerms siteId={siteId} payments={payments} setPayments={update} isCurrent={isCurrent} />
      )}
      {page === "dados-bancarios" && (
        <Registration siteId={siteId} payments={payments} setPayments={update} isCurrent={isCurrent} />
      )}
    </Container>
  );
};

/* At least one screen tall, so the fixed bar sits at the bottom (as the screens of creating a Keepo) */
const FlowContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - var(--app-bottom-nav-height, 0px));
`;

/** Movimentações: the history once the Keepo's payments are known; unavailable or not eligible, why (as the other pages). */
const HistoryPage = ({ siteId, payments }: { siteId: string; payments: PaymentsLoad }) => {
  const { t } = useTranslation();
  if (payments === undefined) {
    return (
      <Section>
        <Card role="status" aria-label={t(`${prefix}.loading`)}>
          <Center>
            <KeepoLoader size={24} color="var(--color-neutral-white)" />
          </Center>
        </Card>
      </Section>
    );
  }
  if (payments === "unavailable" || payments === "error" || !payments.eligible) {
    const message = typeof payments === "string" ? payments : "notEligible";
    return (
      <Section>
        <Card>
          <Message>{t(`${prefix}.${message === "error" ? "loadError" : message}`)}</Message>
        </Card>
      </Section>
    );
  }
  return <History siteId={siteId} reloadKey={0} />;
};

export default ChargesPage;

/* The withdrawal on its way, with its ● yellow dot */
const InProgress = styled.p`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: #ffd00d;

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

/* The same link as the terms of Indique e ganhe: gray and underlined, white on hover */
const TermsButton = styled.button`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);
  text-decoration: underline;
  white-space: nowrap;
  transition: color 0.15s;

  &:hover {
    color: var(--color-neutral-white);
  }
`;
